// apps/web/src/components/conversation-sidebar.tsx
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Input } from '@viecxanh/chat-ui/input'
import { ScrollArea } from '@viecxanh/chat-ui/scroll-area'
import { Badge } from '@viecxanh/chat-ui/badge'
import { Avatar, AvatarFallback } from '@viecxanh/chat-ui/avatar'
import { Skeleton } from '@viecxanh/chat-ui/skeleton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@viecxanh/chat-ui/select'
import { useConversations } from '../hooks/use-conversations'
import { useChatStore } from '../stores/chat-store'
import { OnlineIndicator } from './online-indicator'
import { formatTime } from '../lib/format'
import type { Conversation } from '@viecxanh/chat-shared'

export function ConversationSidebar() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [channelFilter, setChannelFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')

  const { conversations, isLoading, markRead } = useConversations({
    q: search || undefined,
    channel_id: channelFilter || undefined,
    status: (statusFilter as 'open' | 'closed' | 'pending') || undefined,
  })

  const { activeConversationId, setActiveConversation, unreadCounts, onlineUsers } = useChatStore()

  const handleClick = async (conv: Conversation) => {
    setActiveConversation(conv._id)
    await markRead(conv._id)
    void navigate(`/conversations/${conv._id}`)
  }

  return (
    <div className="flex h-full w-72 flex-col border-r bg-white">
      <div className="p-3 border-b space-y-2">
        <Input
          placeholder="Tìm kiếm hội thoại..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8"
        />
        <div className="flex gap-2">
          <Select value={channelFilter} onValueChange={setChannelFilter}>
            <SelectTrigger className="h-7 text-xs flex-1">
              <SelectValue placeholder="Kênh" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tất cả</SelectItem>
              <SelectItem value="facebook">Facebook</SelectItem>
              <SelectItem value="zalo_oa">Zalo OA</SelectItem>
              <SelectItem value="widget">Widget</SelectItem>
              <SelectItem value="internal">Nội bộ</SelectItem>
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-7 text-xs flex-1">
              <SelectValue placeholder="Trạng thái" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tất cả</SelectItem>
              <SelectItem value="open">Mở</SelectItem>
              <SelectItem value="pending">Chờ</SelectItem>
              <SelectItem value="closed">Đóng</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <ScrollArea className="flex-1">
        {isLoading
          ? Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-3 border-b">
                <Skeleton className="h-9 w-9 rounded-full" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-3 w-40" />
                </div>
              </div>
            ))
          : conversations.map((conv) => {
              const isActive = activeConversationId === conv._id
              const unread = unreadCounts[conv._id] ?? 0
              const participantName = conv.participants.find((p) => p.user_type !== 'staff')?.name ?? 'Khách'
              const initials = participantName.slice(0, 2).toUpperCase()
              const isOnline = onlineUsers.some((u) =>
                conv.participants.some((p) => p.user_type === u.user_type && p.user_id === u.user_id),
              )

              return (
                <button
                  key={conv._id}
                  onClick={() => void handleClick(conv)}
                  className={`flex w-full items-center gap-3 px-3 py-3 border-b text-left hover:bg-slate-50 transition-colors ${
                    isActive ? 'bg-green-50 border-l-2 border-l-brand' : ''
                  }`}
                >
                  <div className="relative flex-shrink-0">
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                    </Avatar>
                    <OnlineIndicator isOnline={isOnline} className="absolute -bottom-0.5 -right-0.5" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium truncate">{participantName}</span>
                      {conv.last_message && (
                        <span className="text-[10px] text-slate-400 flex-shrink-0 ml-1">
                          {formatTime(conv.last_message.sent_at)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">
                      {conv.last_message?.content ?? 'Chưa có tin nhắn'}
                    </p>
                  </div>

                  {unread > 0 && (
                    <Badge className="h-5 min-w-5 flex-shrink-0 bg-brand text-white text-[10px]">
                      {unread > 99 ? '99+' : unread}
                    </Badge>
                  )}
                </button>
              )
            })}
      </ScrollArea>
    </div>
  )
}
